import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * A material as the course view and the session tile edit it: one item per
 * listed material, its facts as the row's fields hold them, and what those
 * fields would send.
 *
 * A material sits in one place (a session, the whole course, or the syllabus)
 * and is a draft until its placement is published. An earlier version of a
 * corrected material is history only and is never listed.
 */
import type { ConflictFact } from "@/components/ui/conflict-notice";
import type { CourseUnit } from "@/lib/api/courseUnits";
import type {
  CorrectMaterial,
  Material,
  ResourcePlacement,
  ResourcePlacementRole,
} from "@/lib/api/courseMaterials";
import { inCalendarOrder, unitPlanName } from "@/lib/class-plan-name";
import { GOOGLE_DRIVE_SOURCE_LABEL, googleDriveTypeLabel } from "@/lib/google-drive-labels";
import type { GoogleDriveFailureReason, GoogleDriveSelection } from "@/lib/google-drive-picker";

export const DRIVE_UNSUPPORTED = a3Copy.googleDriveReturnedAFileThatCannotBeUsed;
/**
 * What went wrong when the chooser came back empty, in the person's terms. A
 * refusal, a blocked popup, a missing sign-in and Google not answering each
 * have a different next step, so none of them borrows another's words.
 */
export const DRIVE_FAILURE: Readonly<Record<GoogleDriveFailureReason, string>> = {
  declined: a3Copy.driveDeclined,
  popup_blocked: a3Copy.drivePopupBlocked,
  no_token: a3Copy.driveNoToken,
  unresponsive: a3Copy.driveUnresponsive,
};
export const DRAFT_PLAN_SENTENCE = a3Copy.learnersSeeThisOnceTheSessionPlanIsPublished;

export interface MaterialItem {
  material: Material;
  placement: ResourcePlacement | null;
  role: ResourcePlacementRole;
  published: boolean;
}

/** The listed materials, each with the one placement that names it. */
export function joinMaterials(materials: Material[], placements: ResourcePlacement[]): MaterialItem[] {
  const byMaterial = new Map<string, ResourcePlacement>();
  for (const placement of placements) {
    if (placement.reference_kind === "material" && placement.material_id) {
      byMaterial.set(placement.material_id, placement);
    }
  }
  return materials
    .filter((material) => material.superseded !== true)
    .map((material) => {
      const placement = byMaterial.get(material.id) ?? null;
      return {
        material,
        placement,
        role: placement?.placement_role ?? "course_content",
        published: placement?.publication_status === "published",
      };
    });
}

export interface DriveFile {
  fileId: string;
  resourceKey: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
}

/** A material's facts as the row's fields hold them. `unitId` "" is the whole course. */
export interface MaterialDraft {
  title: string;
  type: string;
  link: string;
  /** The Drive file the link is, or null for a pasted link. */
  drive: DriveFile | null;
  unitId: string;
}

export type MaterialFact = "title" | "type" | "source" | "class";

export function draftOf(material: Material): MaterialDraft {
  return {
    title: material.title,
    type: material.material_type ?? "",
    link: material.file_url ?? "",
    drive: material.google_drive_file_id
      ? {
          fileId: material.google_drive_file_id,
          resourceKey: material.google_drive_resource_key,
          mimeType: material.google_drive_mime_type,
          sizeBytes: material.file_size,
        }
      : null,
    unitId: material.unit_id ?? "",
  };
}

const DRIVE_HOSTS = new Set(["drive.google.com", "docs.google.com"]);

/** The stored address when it is still a Google Drive one, or null. */
export function driveDestination(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const exactPort = parsed.port === "" || parsed.port === "443";
    const bare = parsed.username === "" && parsed.password === "";
    return parsed.protocol === "https:" && exactPort && bare && DRIVE_HOSTS.has(parsed.hostname.toLowerCase()) ? url : null;
  } catch {
    return null;
  }
}

/** "Google Drive · PDF": where the file lives, then what it is. */
export function driveKindLine(mimeType: string | null | undefined): string {
  const kind = googleDriveTypeLabel(mimeType);
  return kind ? `${GOOGLE_DRIVE_SOURCE_LABEL} · ${kind}` : GOOGLE_DRIVE_SOURCE_LABEL;
}

/**
 * A Drive pick as every material write carries it. The one mapping from a
 * Picker selection to IMAM's columns: a session's new reading, a reading's row
 * in Prepare, the material editor and the reading editor all take it from
 * here, so the same pick is the same record wherever it was made. A title
 * already typed wins; a pick without one takes the file's name.
 */
export interface MaterialSourcePayload {
  title: string;
  file_url: string;
  file_size: number | null;
  google_drive_file_id: string;
  google_drive_resource_key: string | null;
  google_drive_mime_type: string;
}

export function selectionToMaterialPayload(selection: GoogleDriveSelection, title = ""): MaterialSourcePayload {
  return {
    title: title.trim() || selection.name,
    file_url: selection.fileUrl,
    file_size: selection.sizeBytes,
    google_drive_file_id: selection.fileId,
    google_drive_resource_key: selection.resourceKey,
    google_drive_mime_type: selection.mimeType,
  };
}

/** The Drive file a mapped pick is, as a row's fields hold it. */
export function driveFileOf(payload: MaterialSourcePayload): DriveFile {
  return {
    fileId: payload.google_drive_file_id,
    resourceKey: payload.google_drive_resource_key,
    mimeType: payload.google_drive_mime_type,
    sizeBytes: payload.file_size,
  };
}

const WEB_LINK = /^https?:\/\/\S+$/i;

export function titleProblem(title: string): string | null {
  return title.trim() ? null : a3Copy.addATitle;
}

export function linkProblem(link: string): string | null {
  return WEB_LINK.test(link.trim()) ? null : a3Copy.useALinkThatStartsWithHttps;
}

/** The content half of a write: what a correction carries and a draft patches. */
export type MaterialContent = Omit<CorrectMaterial, "revision" | "expected_updated_at">;

/** The write that puts a link, and the Drive file it is when it is one, on a material. */
export function sourceContent(link: string, drive: DriveFile | null, wasDrive: boolean): MaterialContent {
  if (drive) {
    return {
      file_url: link,
      file_size: drive.sizeBytes,
      google_drive_file_id: drive.fileId,
      google_drive_resource_key: drive.resourceKey,
      google_drive_mime_type: drive.mimeType,
    };
  }
  return wasDrive
    ? {
        file_url: link,
        file_size: null,
        google_drive_file_id: null,
        google_drive_resource_key: null,
        google_drive_mime_type: null,
      }
    : { file_url: link };
}

const sameSource = (draft: MaterialDraft, base: MaterialDraft) =>
  draft.link.trim() === base.link && (draft.drive?.fileId ?? null) === (base.drive?.fileId ?? null);

/** The facts typed here and not yet saved, sendable or not. */
export function typedFacts(draft: MaterialDraft, base: Material): MaterialFact[] {
  const saved = draftOf(base);
  const facts: MaterialFact[] = [];
  if (draft.title !== saved.title) facts.push("title");
  if (draft.type !== saved.type) facts.push("type");
  if (!sameSource(draft, saved)) facts.push("source");
  if (draft.unitId !== saved.unitId) facts.push("class");
  return facts;
}

/**
 * What the fields hold that the material does not, as the writes send it. A
 * blank title and a link that is not a web address stay in their fields. A
 * syllabus sits on no session, so its session is never sent.
 */
export function changesFrom(
  draft: MaterialDraft,
  base: Material,
  role: ResourcePlacementRole,
): { content: MaterialContent; unitId?: string | null; facts: Set<MaterialFact> } {
  const saved = draftOf(base);
  const content: MaterialContent = {};
  const facts = new Set<MaterialFact>();
  if (!titleProblem(draft.title) && draft.title.trim() !== saved.title) {
    content.title = draft.title.trim();
    facts.add("title");
  }
  const type = draft.type.trim();
  if (type !== saved.type) {
    content.material_type = type || null;
    facts.add("type");
  }
  if (!sameSource(draft, saved) && !linkProblem(draft.link)) {
    Object.assign(content, sourceContent(draft.link.trim(), draft.drive, saved.drive !== null));
    facts.add("source");
  }
  const unitId = role === "course_content" && draft.unitId !== saved.unitId ? draft.unitId || null : undefined;
  if (unitId !== undefined) facts.add("class");
  return { content, unitId, facts };
}

const FACT_LABEL: Record<MaterialFact, string> = {
  title: a3Copy.title,
  type: a3Copy.type,
  source: a3Copy.link,
  class: a3Copy.session,
};

/** A course's sessions for the Session field: by calendar, named as every surface names them. */
export function classOptions(units: CourseUnit[], timezone: string | null): Array<{ id: string; name: string }> {
  return inCalendarOrder(units).map((unit) => ({
    id: unit.id,
    name: a3Copy.sessionPlanState(timezone ? unitPlanName(unit, timezone) : unit.topic, unit.status === "draft" ? a3Copy.planNotPublished : ""),
  }));
}

/** A session's name, or "Whole course" for none. */
export function classLabel(unitId: string, units: CourseUnit[], timezone: string | null): string {
  if (!unitId) return a3Copy.wholeCourse;
  const unit = units.find((candidate) => candidate.id === unitId);
  if (!unit) return "";
  return timezone ? unitPlanName(unit, timezone) : unit.topic;
}

/** Their facts that differ from what was typed, in the row's order. */
export function materialConflictFacts(
  mine: MaterialDraft,
  theirs: Material,
  read: (unitId: string) => string,
  role: ResourcePlacementRole,
): ConflictFact[] {
  const other = draftOf(theirs);
  const value = (draft: MaterialDraft, fact: MaterialFact) => {
    switch (fact) {
      case "title":
        return draft.title.trim();
      case "type":
        return draft.type.trim();
      case "source":
        return draft.link.trim();
      case "class":
        return read(draft.unitId);
    }
  };
  const facts: MaterialFact[] = role === "syllabus" ? ["title", "type", "source"] : ["title", "type", "source", "class"];
  return facts
    .filter((fact) => value(mine, fact) !== value(other, fact))
    .map((fact) => ({ label: FACT_LABEL[fact], theirs: value(other, fact) }));
}
